import axios from "axios";
import { API_URL } from "../config/env";
import { chatFileUrl } from "../utils/chatFiles";

const baseURL = API_URL;

// Create separate axios instance for admin calls that uses adminToken
const adminApi = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor to add admin token (FormData must not use default JSON Content-Type)
adminApi.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Get admin dashboard stats
 * @returns {Promise} Dashboard stats
 */
export const getAdminStats = async () => {
  const response = await adminApi.get("/admin/stats");
  return response.data;
};

/**
 * Get unique IPs and visit counts
 * @returns {Promise} IP statistics
 */
export const getIPs = async () => {
  const response = await adminApi.get("/admin/ips");
  return response.data;
};

/**
 * Get detailed page visits for a specific IP
 * @param {string} ip - IP address
 * @param {number} page - Page number
 * @param {number} limit - Results per page
 * @returns {Promise} IP details
 */
export const getIPDetails = async (ip, page = 1, limit = 100) => {
  const response = await adminApi.get(`/admin/ips/${ip}`, {
    params: { page, limit },
  });
  return response.data;
};

/**
 * Get platform earnings analytics
 * @param {string} startDate - Optional start date filter
 * @param {string} endDate - Optional end date filter
 * @returns {Promise} Earnings data
 */
export const getPlatformEarnings = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const response = await adminApi.get("/admin/earnings", { params });
  return response.data;
};

/**
 * @param {{ status?: string, page?: number, limit?: number }} params
 */
export const getAdminBugReports = async (params = {}) => {
  const response = await adminApi.get("/admin/bug-reports", { params });
  return response.data;
};

export const updateBugReportStatus = async (id, status) => {
  const response = await adminApi.patch(`/admin/bug-reports/${id}`, { status });
  return response.data;
};

export const adminListNews = async () => {
  const response = await adminApi.get("/admin/news");
  return response.data;
};

export const adminCreateNews = async (payload) => {
  const response = await adminApi.post("/admin/news", payload);
  return response.data;
};

export const adminUpdateNews = async (id, payload) => {
  const response = await adminApi.put(`/admin/news/${id}`, payload);
  return response.data;
};

export const adminDeleteNews = async (id) => {
  const response = await adminApi.delete(`/admin/news/${id}`);
  return response.data;
};

export const adminListTutorials = async () => {
  const response = await adminApi.get("/admin/tutorials");
  return response.data;
};

export const adminCreateTutorial = async (payload) => {
  const response = await adminApi.post("/admin/tutorials", payload);
  return response.data;
};

export const adminUpdateTutorial = async (id, payload) => {
  const response = await adminApi.put(`/admin/tutorials/${id}`, payload);
  return response.data;
};

export const adminDeleteTutorial = async (id) => {
  const response = await adminApi.delete(`/admin/tutorials/${id}`);
  return response.data;
};

export const adminGetSiteContent = async () => {
  const response = await adminApi.get("/admin/site-content");
  return response.data;
};

export const adminPutSiteContent = async (payload) => {
  const response = await adminApi.put("/admin/site-content", payload);
  return response.data;
};

/**
 * Upload image to Backblaze B2 (admin JWT). Folder defaults to cms/landing.
 */
export const adminUploadImage = async (file, folder = "cms") => {
  const formData = new FormData();
  formData.append("image", file);
  if (folder) formData.append("folder", folder);
  const response = await adminApi.post("/admin/upload/image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

/**
 * Upload video to Backblaze B2 videos bucket (admin JWT).
 */
/**
 * List submission appeals. status: "open" | "approved" | "denied" | "all"
 */
export const getAdminAppeals = async (status = "open") => {
  const response = await adminApi.get("/admin/appeals", { params: { status } });
  return response.data;
};

/**
 * Resolve an appeal (decision: "approved" | "denied").
 */
export const resolveAdminAppeal = async (submissionId, decision, notes = "") => {
  const response = await adminApi.post(`/admin/appeals/${submissionId}/resolve`, {
    decision,
    notes,
  });
  return response.data;
};

export const adminUploadVideo = async (file, folder = "cms/videos") => {
  const formData = new FormData();
  formData.append("video", file);
  if (folder) formData.append("folder", folder);
  const response = await adminApi.post("/admin/upload/video", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

/* ------------------------------------------------------------------ payments */

/**
 * Every transaction, newest first.
 *
 * Refunds here need nothing from the buyer — Keepz returns the money to whatever they
 * paid with, and Mintly's own records are settled in the same call.
 *
 * @param {{ q?, type?, status?, from?, to?, page?, limit? }} params
 * @see mintly-back/src/controllers/adminPaymentsController
 */
export const getAdminTransactions = async (params = {}) => {
  const response = await adminApi.get("/admin/transactions", { params });
  return response.data;
};

/** Refund one product purchase in full. */
export const adminRefundTransaction = async (orderId) => {
  const response = await adminApi.post(
    `/admin/transactions/${encodeURIComponent(orderId)}/refund`
  );
  return response.data;
};

/** Products with sales, for refunding every buyer of one. */
export const getAdminSoldProducts = async (q = "") => {
  const response = await adminApi.get("/admin/sold-products", { params: { q } });
  return response.data;
};

/**
 * Refund every buyer of a product.
 * @param {boolean} confirm - false only reports what would happen
 */
export const adminRefundProduct = async (productId, confirm = false) => {
  const response = await adminApi.post(`/admin/products/${productId}/refund`, { confirm });
  return response.data;
};

/* ---------------------------------------------------------------------- fees */

/**
 * The rates Mintly charges and Keepz costs us, per payment method.
 * @see mintly-back/src/controllers/revenueController
 */
export const getAdminFees = async () => {
  const response = await adminApi.get("/admin/fees");
  return response.data;
};

/**
 * Change those rates. A rate sent as null is cleared and inherits again.
 * @param {{ saleFeePercent?, creatorFeePercent?, referralPercent?, methods? }} patch
 */
export const updateAdminFees = async (patch) => {
  const response = await adminApi.put("/admin/fees", patch);
  return response.data;
};

/* ---------------------------------------------------------------------- users */

/**
 * Users, with their trust level and what they're owed.
 * @param {{ q?, level?, blocked?, page?, limit? }} params
 * @see mintly-back/src/controllers/adminUsersController
 */
export const getAdminUsers = async (params = {}) => {
  const response = await adminApi.get("/admin/users", { params });
  return response.data;
};

/**
 * One user's money: what they earned, what is still on hold, what they withdrew and
 * what they bought.
 */
export const getAdminUser = async (id) => {
  const response = await adminApi.get(`/admin/users/${id}`);
  return response.data;
};

/** Block or unblock someone. A blocked account can't sign in, and open sessions stop. */
export const setAdminUserBlocked = async (id, blocked, reason = "") => {
  const response = await adminApi.patch(`/admin/users/${id}/block`, { blocked, reason });
  return response.data;
};

/** Move someone to another trust level. Money already on hold is re-timed to it. */
export const setAdminUserLevel = async (id, level) => {
  const response = await adminApi.patch(`/admin/users/${id}/level`, { level });
  return response.data;
};

/** How many days each level waits before earnings can be withdrawn. */
export const getAdminHoldPolicy = async () => {
  const response = await adminApi.get("/admin/hold-policy");
  return response.data;
};

/** @param {Record<string, number|null>} holdDays - keyed by level: { "0": 30, ... } */
export const updateAdminHoldPolicy = async (holdDays) => {
  const response = await adminApi.put("/admin/hold-policy", { holdDays });
  return response.data;
};

/**
 * Businesses, with where their KYB stands.
 * @param {{ q?, kyb?: "pending"|"approved"|"declined"|"none", page?, limit? }} params
 * @see mintly-back/src/controllers/adminKycController
 */
export const getAdminBusinesses = async (params = {}) => {
  const response = await adminApi.get("/admin/businesses", { params });
  return response.data;
};

/** Everything a business submitted for its KYB — an individual's answers or a company's. */
export const getAdminBusinessKyb = async (id) => {
  const response = await adminApi.get(`/admin/businesses/${id}/kyb`);
  return response.data;
};

/** Approve, decline or reopen a business's KYB. Approving or declining notifies the owner. */
export const setAdminBusinessKybStatus = async (id, status, reason = "") => {
  const response = await adminApi.patch(`/admin/businesses/${id}/kyb`, { status, reason });
  return response.data;
};

/**
 * Uploads the nudity filter refused, newest first, with counts and the thresholds they
 * were measured against. The files themselves are never kept.
 * @param {{ kind?: "image"|"video", page?, limit? }} params
 * @see mintly-back/src/controllers/adminBlockedUploadsController
 */
export const getAdminBlockedUploads = async (params = {}) => {
  const response = await adminApi.get("/admin/blocked-uploads", { params });
  return response.data;
};

/** Campaigns for picking Discover's Featured row: picks first, then newest. */
export const getAdminCampaigns = async (params = {}) => {
  const response = await adminApi.get("/admin/campaigns", { params });
  return response.data;
};

/** Feature a campaign in Discover, or take it out. */
export const setAdminCampaignFeatured = async (id, featured) => {
  const response = await adminApi.patch(`/admin/campaigns/${id}/featured`, { featured });
  return response.data;
};

/**
 * Support chat: everyone's conversations with Mintly support, unread first.
 * @param {{ status?: "open"|"closed"|"all", q?: string, page?: number, limit?: number }} params
 * @see mintly-back/src/controllers/adminSupportController
 */
export const getAdminSupportConversations = async (params = {}) => {
  const response = await adminApi.get("/admin/support", { params });
  return response.data;
};

/** One conversation and its latest messages (or those before `before`); reads it for every admin. */
export const getAdminSupportConversation = async (id, before = null) => {
  const response = await adminApi.get(`/admin/support/${id}`, { params: before ? { before } : {} });
  return response.data;
};

export const markAdminSupportRead = async (id) => {
  const response = await adminApi.post(`/admin/support/${id}/read`);
  return response.data;
};

export const sendAdminSupportReply = async (id, content) => {
  const response = await adminApi.post(`/admin/support/${id}/messages`, { content });
  return response.data;
};

/** Reply with a photo, video or file, and an optional caption. */
export const sendAdminSupportAttachment = async (id, file, content = "", onProgress) => {
  const form = new FormData();
  if (content) form.append("content", content);
  form.append("fileName", file.name);
  form.append("file", file);
  const response = await adminApi.post(`/admin/support/${id}/attachments`, form, {
    onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0),
  });
  return response.data;
};

/** Where a file in a support conversation is shown from, signed in as the admin. */
export const adminSupportFileUrl = (id, messageId, { download = false } = {}) =>
  chatFileUrl(`/admin/support/${id}/messages/${messageId}/file`, { download, tokenKey: "adminToken" });

/** @param {"open"|"closed"} status */
export const setAdminSupportStatus = async (id, status) => {
  const response = await adminApi.patch(`/admin/support/${id}`, { status });
  return response.data;
};
